import type { FormEvent, MouseEvent } from "react";
import { useState } from "react";
import type { User } from "../api";
import { api } from "../api";

type Props = {
  token: string;
  user: User;
  onClose: () => void;
  onSaved?: () => void;
};

export function SetPasswordModal({ token, user, onClose, onSaved }: Props) {
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const onBackdrop = (ev: MouseEvent<HTMLDivElement>) => {
    if (ev.target === ev.currentTarget && !busy) onClose();
  };

  const onSubmit = async (ev: FormEvent) => {
    ev.preventDefault();
    setError(null);
    if (pw1.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (pw1 !== pw2) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.setPassword(token, user.id, pw1);
      setDone(true);
      onSaved?.();
    } catch (e: unknown) {
      const msg =
        typeof e === "object" && e && "message" in e ? String((e as { message?: string }).message) : String(e);
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={onBackdrop}
      role="presentation"
    >
      <div
        className="bg-white rounded-lg border border-slate-200 shadow-xl max-w-md w-full flex flex-col"
        role="dialog"
        aria-labelledby="set-password-modal-title"
        onMouseDown={(ev) => ev.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 id="set-password-modal-title" className="text-base font-semibold text-slate-900">
              Set password
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              User <span className="font-mono">{user.username}</span> (id {user.id})
            </p>
          </div>
          <button type="button" onClick={onClose} className="btn shrink-0" disabled={busy} aria-label="Close">
            Close
          </button>
        </div>

        <form className="p-4 space-y-3" onSubmit={(ev) => void onSubmit(ev)}>
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
          )}
          {done && !error && (
            <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
              Password updated. The user's existing JWTs continue to work until they expire; new logins use the new
              password.
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">New password (min 8 chars)</label>
            <input
              type="password"
              className="input"
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              required
              minLength={8}
              autoFocus
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Confirm new password</label>
            <input
              type="password"
              className="input"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />
          </div>

          <div className="pt-2 flex justify-end gap-2 border-t border-slate-100">
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              {done ? "Done" : "Cancel"}
            </button>
            <button type="submit" className="btn-primary" disabled={busy || done}>
              {busy ? "Saving…" : done ? "Saved" : "Set password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
